import {
  BdcCreditableInvoice,
  BdcCreditableItem,
  BdcCreditableSearchParams,
  BdcCreditNoteDetail,
  BdcCreditNoteListData,
  BdcCreditNoteListParams,
  BdcCreditNoteSaveRequest,
  BdcListShape,
} from "../types/bdc.type";
import { ApiResponse } from "../types/express-data.type";
import axios from "./axios";

/**
 * BDC · CN ใบลดหนี้ (P2 C1–C5) — `/bdc/credit-notes`.
 *
 *   GET    /credit-notes                  list (filters like the INV register) · { rows, count }
 *   GET    /credit-notes/export           ทะเบียน CN xlsx (same filters)
 *   GET    /credit-notes/:uuid            head + items
 *   GET    /credit-notes/:uuid/pdf        ใบลดหนี้ PDF (DocGen)
 *   POST   /credit-notes                  new draft
 *   PUT    /credit-notes/:uuid            replace a draft
 *   DELETE /credit-notes/:uuid            delete a draft
 *   POST   /credit-notes/:uuid/issue      number it (C2… / TC2…)
 *   POST   /credit-notes/:uuid/cancel     { reason } — not once in a RETSEV file or on a ใบวางบิล
 *   GET    /invoices/:invoice_no/creditable   what each product of an INV can still be credited
 *   GET    /invoices/creditable-search        the DC's INVs that can still be credited
 *
 * Read: the BDC cohort + MASTER · write: operator and up (as the INV). Refusals carry `error.code`
 * (BDC_CREDIT_NOTE_*); save / issue show them on the screen, so their global toast is off.
 */

const ownErrors = { suppressForbiddenRedirect: true, suppressErrorToast: true } as const;
const notePath = (uuid: string) => `/bdc/credit-notes/${encodeURIComponent(uuid)}`;

const buildCreditNoteQuery = (params: Omit<BdcCreditNoteListParams, "page" | "limit">) => {
  const query = new URLSearchParams();
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  if (params.status) query.append("status", params.status);
  if (params.search) query.append("search", params.search);
  if (params.company_id) query.append("company_id", params.company_id);
  if (params.is_test !== undefined) query.append("is_test", String(params.is_test));
  return query;
};

export const listBdcCreditNotes = (params: BdcCreditNoteListParams) => {
  const query = buildCreditNoteQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get<ApiResponse<BdcCreditNoteListData>>(`/bdc/credit-notes?${query.toString()}`);
};

/** Excel ทะเบียน CN (C5) — the filters of the list. */
export const exportBdcCreditNotes = (params: Omit<BdcCreditNoteListParams, "page" | "limit">) =>
  axios.get<Blob>(`/bdc/credit-notes/export?${buildCreditNoteQuery(params).toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });

export const getBdcCreditNote = (uuid: string) => axios.get<ApiResponse<BdcCreditNoteDetail>>(notePath(uuid));

/** ใบลดหนี้ PDF (DocGen) — ทดสอบ watermark for TC…, ยกเลิก stamp when cancelled. */
export const getBdcCreditNotePdf = (uuid: string) =>
  axios.get<Blob>(`${notePath(uuid)}/pdf`, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        404: "ไม่พบ CN นี้",
        409: "CN ที่ยังเป็นร่างยังไม่มีเอกสาร — ออก CN ก่อน",
        502: "ระบบสร้างเอกสารตอบช้าเกินไป กรุณาลองใหม่อีกครั้ง",
        503: "ระบบสร้างเอกสารไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });

export const createBdcCreditNote = (body: BdcCreditNoteSaveRequest) =>
  axios.post<ApiResponse<BdcCreditNoteDetail>>(`/bdc/credit-notes`, body, { meta: ownErrors });

export const updateBdcCreditNote = (uuid: string, body: BdcCreditNoteSaveRequest) =>
  axios.put<ApiResponse<BdcCreditNoteDetail>>(notePath(uuid), body, { meta: ownErrors });

export const deleteBdcCreditNote = (uuid: string) =>
  axios.delete<ApiResponse<null>>(notePath(uuid), {
    meta: { successMsg: "ลบร่าง CN แล้ว", suppressForbiddenRedirect: true },
  });

export const issueBdcCreditNote = (uuid: string) =>
  axios.post<ApiResponse<BdcCreditNoteDetail>>(`${notePath(uuid)}/issue`, {}, { meta: ownErrors });

export const cancelBdcCreditNote = (uuid: string, reason: string) =>
  axios.post<ApiResponse<BdcCreditNoteDetail>>(
    `${notePath(uuid)}/cancel`,
    { reason },
    { meta: { successMsg: "ยกเลิก CN แล้ว", suppressForbiddenRedirect: true } },
  );

export const getBdcCreditableItems = (invoiceNo: string) =>
  axios.get<ApiResponse<BdcListShape<BdcCreditableItem>>>(
    `/bdc/invoices/${encodeURIComponent(invoiceNo)}/creditable`,
    { meta: { suppressForbiddenRedirect: true } },
  );

export const searchBdcCreditableInvoices = (params: BdcCreditableSearchParams) => {
  const query = new URLSearchParams({ dc_code: params.dc_code });
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);
  if (params.search) query.append("search", params.search);
  return axios.get<ApiResponse<BdcListShape<BdcCreditableInvoice>>>(
    `/bdc/invoices/creditable-search?${query.toString()}`,
  );
};
